import { Inning } from "../matchTypes";

export interface Partnership {
  wicketNumber: number;
  batter1Id: string;
  batter2Id: string;
  runs: number;
  balls: number;
  batter1Runs: number;
  batter1Balls: number;
  batter2Runs: number;
  batter2Balls: number;
  isCurrent: boolean;
  endedByWicket: boolean;
}

/**
 * Derives all partnerships for a single innings from the stored delivery
 * history. Reads the same authoritative delivery array used by the rest
 * of the app. Does NOT modify any match state or scoring engine.
 *
 * Algorithm: walk deliveries in order, maintain the current pair at the
 * crease (striker + non-striker), accumulate runs/balls per partnership.
 * On a wicket, close the partnership and swap in the new batter. The last
 * unclosed pair is the current partnership.
 *
 * Runs are attributed to the striker on strike for each delivery using
 * the same rotation rules as the engine (odd runs swap, over-end swaps).
 * Byes/leg-byes add to the partnership total but not to the batter.
 * Wides/no-balls add to the partnership total but not to balls faced.
 * Run outs close the partnership; the dismissed batter is identified by
 * delivery.batterId (the scoring UI sets this to the selected batter for
 * Run Out, and to state.strikerId for all other dismissals).
 *
 * battersCrossed (Run Out / Caught) and over-end swaps are applied after
 * wickets to match the engine's strike rotation behaviour.
 *
 * LIMITATION: Retirements (retireHurtBatter / returnRetiredBatter) do not
 * create deliveries, so partnership changes due to retirement are NOT
 * detectable from the delivery history alone. The partnership will
 * incorrectly continue across a retirement boundary. This is a known
 * limitation — fixing it would require adding retirement records to the
 * delivery history or a separate partnership-change event log.
 */
export function derivePartnerships(inn: Inning, ballsPerOver = 6): Partnership[] {
  const partnerships: Partnership[] = [];
  if (!inn.deliveries.length) return partnerships;

  let strikerId: string | null = null;
  let nonStrikerId: string | null = null;
  let wicketNumber = 0;
  let currentRuns = 0;
  let currentBalls = 0;
  let batterStats: Record<string, { runs: number; balls: number }> = {};
  let legalBallCount = 0;

  const closePartnership = (endedByWicket: boolean) => {
    if (!strikerId || !nonStrikerId) return;
    wicketNumber++;
    const s1 = batterStats[strikerId] || { runs: 0, balls: 0 };
    const s2 = batterStats[nonStrikerId] || { runs: 0, balls: 0 };
    partnerships.push({
      wicketNumber,
      batter1Id: strikerId,
      batter2Id: nonStrikerId,
      runs: currentRuns,
      balls: currentBalls,
      batter1Runs: s1.runs,
      batter1Balls: s1.balls,
      batter2Runs: s2.runs,
      batter2Balls: s2.balls,
      isCurrent: false,
      endedByWicket,
    });
  };

  for (let i = 0; i < inn.deliveries.length; i++) {
    const delivery = inn.deliveries[i];

    // Establish the initial pair from the first two batters
    if (!strikerId && delivery.batterId) strikerId = delivery.batterId;
    if (!nonStrikerId && delivery.batterId && delivery.batterId !== strikerId) {
      nonStrikerId = delivery.batterId;
    }
    if (!strikerId || !nonStrikerId) continue;

    // Initialize batter stats if needed
    if (!batterStats[strikerId]) batterStats[strikerId] = { runs: 0, balls: 0 };
    if (!batterStats[nonStrikerId]) batterStats[nonStrikerId] = { runs: 0, balls: 0 };

    const isByeOrLegbye = delivery.extraType === "bye" || delivery.extraType === "legbye";

    // Add runs to partnership total
    currentRuns += delivery.runs;

    // Attribute runs to striker (not for byes/leg-byes)
    if (!isByeOrLegbye && strikerId) {
      const batterRuns =
        delivery.extraType === "wide" || delivery.extraType === "noball"
          ? Math.max(0, delivery.runs - 1)
          : delivery.runs;
      batterStats[strikerId].runs += batterRuns;
    }

    // Count legal balls faced by striker
    if (delivery.isLegal && strikerId) {
      currentBalls += 1;
      batterStats[strikerId].balls += 1;
      legalBallCount++;
    }

    // Handle wicket
    if (delivery.isWicket) {
      const dismissedId = delivery.batterId || strikerId;
      closePartnership(true);

      // Reset accumulators for next partnership
      currentRuns = 0;
      currentBalls = 0;
      batterStats = {};

      // The dismissed batter leaves; the other stays
      if (dismissedId === strikerId) {
        strikerId = null;
      } else {
        nonStrikerId = null;
      }

      // Apply over-end swap for legal deliveries (engine does this for
      // ALL legal deliveries including wickets, line 295-300)
      if (delivery.isLegal && legalBallCount % ballsPerOver === 0) {
        const temp = strikerId;
        strikerId = nonStrikerId;
        nonStrikerId = temp;
      }

      // Apply crossing swap for Run Out / Caught (LiveScore.tsx line 370-374
      // applies this AFTER addDelivery returns, so after the over-end swap)
      if (delivery.battersCrossed) {
        const temp = strikerId;
        strikerId = nonStrikerId;
        nonStrikerId = temp;
      }

      continue;
    }

    // Strike rotation: odd runs swap (same logic as engine, line 277-293)
    if (delivery.isLegal || delivery.extraType === "noball") {
      const runsForRotation = delivery.runsForRotation ?? delivery.runs;
      const effectiveRuns = delivery.wallBonus
        ? 1
        : delivery.extraType === "noball"
          ? Math.max(0, delivery.runs - 1)
          : runsForRotation;
      if (effectiveRuns === 1 || effectiveRuns === 3 || effectiveRuns === 5) {
        const temp = strikerId;
        strikerId = nonStrikerId;
        nonStrikerId = temp;
      }
    }

    // End of over swap (engine line 295-300, applies to all legal deliveries)
    if (delivery.isLegal && legalBallCount % ballsPerOver === 0) {
      const temp = strikerId;
      strikerId = nonStrikerId;
      nonStrikerId = temp;
    }
  }

  // Close the current (unclosed) partnership
  if (strikerId && nonStrikerId && (currentBalls > 0 || currentRuns > 0)) {
    closePartnership(false);
    partnerships[partnerships.length - 1].isCurrent = true;
  }

  return partnerships;
}
